/**
 * D-017 — jedno miejsce prawdy dla podkładu map labu (bez Leafleta, więc
 * importowalne w testach node): adresy kafli, zestawy teł, atrybucje
 * i reguła widoczności nazw miast.
 *
 * CONUS (`/`, `/us`): USGS The National Map (rządowe, domena publiczna, bez
 * klucza) + własne wektory Natural Earth z `public/basemap/` (granice stanów,
 * brzeg, miasta). Świat (`/explorer`, region EUROPA na `/`): NASA GIBS
 * EPSG:3857 (bez klucza; warstwa ląd/woda pochodzi z OpenStreetMap).
 * Powrót do innego dostawcy = zmiana w tym pliku.
 */

// ---------- USGS The National Map ----------
/** Uwaga: ArcGIS MapServer ma kolejność {z}/{y}/{x}. */
export function usgsTileUrl(service: string): string {
  return `https://basemap.nationalmap.gov/arcgis/rest/services/${service}/MapServer/tile/{z}/{y}/{x}`;
}

export const USGS_SERVICES = {
  shadedRelief: "USGSShadedReliefOnly",
  hydro: "USGSHydroCached",
  topo: "USGSTopo",
  imagery: "USGSImageryOnly",
} as const;

// ---------- NASA GIBS (EPSG:3857) ----------
export const GIBS_MAX_NATIVE_ZOOM = 8; // GoogleMapsCompatible_Level9 = z0–z8

export function gibsTileUrl(layer: string): string {
  return `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/${layer}/default/GoogleMapsCompatible_Level9/{z}/{y}/{x}.png`;
}

export const GIBS_LAYERS = {
  landWater: "OSM_Land_Water_Map",
  features: "Reference_Features",
  labels: "Reference_Labels",
} as const;

// ---------- definicje warstw ----------
export interface TileDef {
  url: string;
  /** Klasa CSS kontenera warstwy (np. filtr motywu ciemnego). */
  className?: string;
  maxNativeZoom?: number;
}

/** Filtr „ciemnej" wersji rzeźby USGS (EFEKT wizualny, nie osobny produkt) — patrz basemap.css. */
export const DARK_FILTER_CLASS = "bm-tiles-dark";
/** GIBS ląd/woda w oryginale: ciemnoszary ląd (#4b4b4b), jaśniejsza woda — odwracamy do jasnego tła (basemap.css). */
export const GIBS_LIGHT_FILTER_CLASS = "bm-tiles-gibs-light";

export type VectorTheme = "light" | "dark";

export interface VectorSet {
  /** Granice stanów i lądowe granice USA (`us-states.geojson`). */
  borders: boolean;
  /** Linia brzegowa i jeziora (`us-coast.geojson`). */
  coast: boolean;
  /** Nazwy miast (`us-cities.geojson`). */
  cities: boolean;
  theme: VectorTheme;
}

export type ConusStyleId = "jasna" | "ciemna" | "kolor" | "satelita";

export interface ConusStyle {
  id: ConusStyleId;
  label: string;
  tiles: TileDef[];
  /** null = bez naszych wektorów (tło ma własne etykiety). */
  vectors: VectorSet | null;
  /** Pełna atrybucja (panel „o danych"). */
  attribution: string;
  /** Krótka atrybucja zawsze widoczna na mapie. */
  attributionShort: string;
}

const USGS_PD = "USGS The National Map (domena publiczna)";
const NE_PD = "granice i miasta: Natural Earth (domena publiczna)";

/** Atrybucja podkładu CONUS (tekst). */
export const CONUS_ATTRIBUTION = `${USGS_PD} · ${NE_PD}`;
/** Ta sama atrybucja z linkami — dla kontrolki atrybucji Leafleta. */
export const CONUS_ATTRIBUTION_HTML =
  '<a href="https://www.usgs.gov/programs/national-geospatial-program/national-map" target="_blank" rel="noopener">USGS The National Map</a> (domena publiczna) · granice i miasta: <a href="https://www.naturalearthdata.com/" target="_blank" rel="noopener">Natural Earth</a> (domena publiczna)';

/** Atrybucja podkładu świata (explorer, region EUROPA na `/`). */
export const EXPLORER_ATTRIBUTION = "NASA GIBS · © OpenStreetMap contributors";
export const EXPLORER_ATTRIBUTION_HTML =
  '<a href="https://earthdata.nasa.gov/gibs" target="_blank" rel="noopener">NASA GIBS</a> · © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors';

const RELIEF_STACK: TileDef[] = [{ url: usgsTileUrl(USGS_SERVICES.shadedRelief) }, { url: usgsTileUrl(USGS_SERVICES.hydro) }];

export const CONUS_STYLES: Record<ConusStyleId, ConusStyle> = {
  jasna: {
    id: "jasna",
    label: "jasna",
    tiles: RELIEF_STACK,
    vectors: { borders: true, coast: false, cities: true, theme: "light" },
    attribution: `rzeźba i wody: ${USGS_PD} · ${NE_PD}`,
    attributionShort: "Tło: USGS The National Map · Natural Earth",
  },
  ciemna: {
    id: "ciemna",
    label: "ciemna",
    tiles: RELIEF_STACK.map((t) => ({ ...t, className: DARK_FILTER_CLASS })),
    vectors: { borders: true, coast: false, cities: true, theme: "dark" },
    attribution: `rzeźba i wody: ${USGS_PD}, przyciemnione u nas filtrem kolorów (efekt wizualny, nie osobny produkt) · ${NE_PD}`,
    attributionShort: "Tło: rzeźba USGS z filtrem kolorów · Natural Earth",
  },
  kolor: {
    id: "kolor",
    label: "kolor",
    tiles: [{ url: usgsTileUrl(USGS_SERVICES.topo) }],
    vectors: null,
    attribution: `${USGS_PD} — mapa topograficzna z własnymi etykietami USGS`,
    attributionShort: "Tło: USGS The National Map (Topo)",
  },
  satelita: {
    id: "satelita",
    label: "satelita",
    tiles: [{ url: usgsTileUrl(USGS_SERVICES.imagery) }],
    vectors: { borders: true, coast: true, cities: true, theme: "dark" },
    attribution: `zdjęcia: ${USGS_PD} — Imagery · ${NE_PD}`,
    attributionShort: "Tło: zdjęcia USGS · Natural Earth",
  },
};

/** Stos świata (GIBS): ląd/woda (rozjaśniony filtrem) + granice/brzegi + nazwy. */
export const GIBS_STACK: TileDef[] = [
  { url: gibsTileUrl(GIBS_LAYERS.landWater), className: GIBS_LIGHT_FILTER_CLASS, maxNativeZoom: GIBS_MAX_NATIVE_ZOOM },
  { url: gibsTileUrl(GIBS_LAYERS.features), maxNativeZoom: GIBS_MAX_NATIVE_ZOOM },
  { url: gibsTileUrl(GIBS_LAYERS.labels), maxNativeZoom: GIBS_MAX_NATIVE_ZOOM },
];

/** Pliki wektorów (Vite kopiuje `public/` do `dist/`). */
export const VECTOR_FILES = {
  borders: "basemap/us-states.geojson",
  coast: "basemap/us-coast.geojson",
  cities: "basemap/us-cities.geojson",
} as const;

// ---------- miasta: widoczność od zoomu + prosta reguła bez nakładania ----------
export interface CityPoint {
  name: string;
  /** NE scalerank (0 = najważniejsze). */
  rank: number;
  pop_max: number;
  lat: number;
  lon: number;
}

/** Minimalny zoom, od którego miasto o danej randze NE może się pokazać. */
export function cityMinZoom(rank: number): number {
  if (rank <= 1) return 3;
  if (rank <= 2) return 4;
  if (rank <= 3) return 5;
  if (rank <= 4) return 6;
  return 7;
}

/** Przybliżony prostokąt etykiety (kropka po lewej, tekst 12 px po prawej). */
export function labelBox(x: number, y: number, name: string): { x0: number; y0: number; x1: number; y1: number } {
  const PAD = 3;
  return { x0: x - 4 - PAD, y0: y - 8 - PAD, x1: x + 8 + name.length * 6.6 + PAD, y1: y + 8 + PAD };
}

/**
 * Zachłanny wybór etykiet bez nakładania: miasta w kolejności ważności
 * (rank rosnąco, pop_max malejąco); miasto wchodzi, jeśli jego prostokąt nie
 * nachodzi na żaden już przyjęty. `project` = współrzędne pikselowe przy
 * danym zoomie (np. `map.project`). Wynik zależy tylko od zoomu, nie od
 * przesunięcia mapy, więc liczymy go raz na poziom zoomu.
 */
export function selectCityLabels(cities: CityPoint[], zoom: number, project: (lat: number, lon: number) => { x: number; y: number }): CityPoint[] {
  const sorted = cities
    .filter((c) => zoom >= cityMinZoom(c.rank))
    .slice()
    .sort((a, b) => a.rank - b.rank || b.pop_max - a.pop_max || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  const placed: { x0: number; y0: number; x1: number; y1: number }[] = [];
  const out: CityPoint[] = [];
  for (const c of sorted) {
    const p = project(c.lat, c.lon);
    const b = labelBox(p.x, p.y, c.name);
    if (placed.some((q) => b.x0 < q.x1 && b.x1 > q.x0 && b.y0 < q.y1 && b.y1 > q.y0)) continue;
    placed.push(b);
    out.push(c);
  }
  return out;
}
